/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {getOtaStatCode} from '@ion/core/src/services/Connect';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGetOtaStateResult = UseDefaultQueryResult & {
  statusCode;
};

const PERSISTENCE_KEY = 'service:ota:stat';

/**
 * The useGetOtaState hook provides a Query for retreiving ota states.
 *
 * ```typescript
 *
 * import {useGetOtaState} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {statusCode} = useGetOtaState();
 *
 * ```
 *
 * import {useGetOtaState} from '@ion/query/src/hooks/';
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseGetOtaStateResult  containing the query results and the ota states.
 */
export const useGetOtaState = (
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseGetOtaStateResult => {
  const query = useQuery([QueryKey.OTA_STATE],
    async() => {
      const cache = getLocalStorage(PERSISTENCE_KEY);
      if (!cache) {
        const results = await getOtaStatCode();
        setLocalStorage(PERSISTENCE_KEY, results);
        return results;
      }
      return cache;
    }, {
      staleTime : staleTime as number,
      enabled
    }
  );

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    statusCode : query.data || {}
  }) as UseGetOtaStateResult;
};

export default useGetOtaState;
